/* eslint-disable */

import {
    Box,
    Flex,
    Icon,
    Table,
    Tbody,
    HStack,
    Avatar,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Button,
    Select,
    useColorModeValue,
    Badge,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    VStack,
    Divider,
    useToast,
} from "@chakra-ui/react";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";
import Card from "components/card/Card";
import { NavLink } from "react-router-dom";
import * as React from "react";
import { MdCancel, MdCheckCircle, MdOutlineError, MdContentCopy, MdDownload, MdReceipt } from "react-icons/md";
import { CopyIcon, ViewIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import jsPDF from 'jspdf';
import constants from "providers/Constants";

const columnHelper = createColumnHelper();

// Transaction Details Modal Component for Crypto Transactions
function TransactionDetailsModal({ isOpen, onClose, transaction, t }) {
    const bgColor = useColorModeValue('white', 'navy.800');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const receiptBg = useColorModeValue('gray.50', 'navy.900');
    const toast = useToast();
    const [showReceipt, setShowReceipt] = React.useState(false);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const receiptRef = React.useRef(null);

    if (!transaction) return null;

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast({
            title: t('Copied!'),
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const formatDate = (dateString) => {
        if (!dateString || isNaN(Date.parse(dateString))) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatAmount = (amount, symbol) => {
        const num = Number(amount);
        if (isNaN(num)) return 'N/A';
        return `${symbol || ''} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`;
    };

    const DetailRow = ({ label, value, copyable = false }) => (
        <Flex justify="space-between" align="center" py={2}>
            <Text color={secondaryTextColor} fontSize="sm">{label}</Text>
            <HStack>
                <Text color={textColor} fontSize="sm" fontWeight="600" textAlign="right" maxW="200px" isTruncated>
                    {value || 'N/A'}
                </Text>
                {copyable && value && (
                    <IconButton
                        size="xs"
                        variant="ghost"
                        icon={<Icon as={MdContentCopy} />}
                        onClick={() => copyToClipboard(value)}
                        aria-label="Copy"
                    />
                )}
            </HStack>
        </Flex>
    );

    const getStatusColor = () => {
        return transaction.confirmations >= 1 ? 'green' : 'orange';
    };

    const getStatusText = () => {
        return transaction.confirmations >= 1 ? t('Confirmed') : t('Pending');
    };

    const downloadReceipt = async () => {
        setIsDownloading(true);
        try {
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const margin = 20;
            const contentWidth = pageWidth - (margin * 2);
            let yPos = margin;

            // Helper function to load image as base64 using fetch
            const loadImage = async (url) => {
                try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    
                    return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = () => resolve(null);
                        reader.readAsDataURL(blob);
                    });
                } catch (error) {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.crossOrigin = 'Anonymous';
                        img.onload = () => {
                            try {
                                const canvas = document.createElement('canvas');
                                canvas.width = img.width;
                                canvas.height = img.height;
                                const ctx = canvas.getContext('2d');
                                ctx.drawImage(img, 0, 0);
                                resolve(canvas.toDataURL('image/png'));
                            } catch (e) {
                                resolve(null);
                            }
                        };
                        img.onerror = () => resolve(null);
                        img.src = url;
                    });
                }
            };

            // Helper function to center text
            const centerText = (text, y, fontSize = 12, fontStyle = 'normal') => {
                pdf.setFontSize(fontSize);
                pdf.setFont('helvetica', fontStyle);
                const textWidth = pdf.getTextWidth(text);
                pdf.text(text, (pageWidth - textWidth) / 2, y);
            };

            // Helper function to add a line of text with label and value
            const addDetailRow = (label, value, y, wrapLongValue = false) => {
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(100, 100, 100);
                pdf.text(label, margin + 10, y);
                
                pdf.setTextColor(30, 30, 30);
                pdf.setFont('helvetica', 'bold');
                const valueText = String(value || 'N/A');
                
                if (wrapLongValue) {
                    // For long values (like txid, address), always place on next line
                    y += 6;
                    pdf.setFontSize(9);
                    pdf.setFont('helvetica', 'normal');
                    pdf.text(valueText, margin + 10, y);
                    return y + 8;
                } else {
                    const valueWidth = pdf.getTextWidth(valueText);
                    pdf.text(valueText, pageWidth - margin - 10 - valueWidth, y);
                    return y + 8;
                }
            };

            // Helper to draw avatar placeholder circle with initial
            const drawAvatarPlaceholder = (x, y, size, initial) => {
                pdf.setFillColor(100, 100, 220);
                pdf.circle(x + size / 2, y + size / 2, size / 2, 'F');
                pdf.setTextColor(255, 255, 255);
                pdf.setFontSize(12);
                pdf.setFont('helvetica', 'bold');
                const textWidth = pdf.getTextWidth(initial);
                pdf.text(initial, x + (size - textWidth) / 2, y + size / 2 + 2);
            };

            // Draw receipt border (height will be adjusted based on content)
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineDashPattern([2, 2], 0);
            const receiptHeight = 285; // Increased to accommodate full transaction ID and address on separate lines
            pdf.roundedRect(margin, margin, contentWidth, receiptHeight, 3, 3, 'S');

            yPos = margin + 10;

            // Add avatar
            const avatarSize = 20;
            let avatarLoaded = false;
            
            if (transaction.avatar) {
                try {
                    const avatarData = await loadImage(transaction.avatar);
                    if (avatarData && avatarData.startsWith('data:')) {
                        const format = avatarData.includes('image/png') ? 'PNG' : 'JPEG';
                        pdf.addImage(avatarData, format, (pageWidth - avatarSize) / 2, yPos, avatarSize, avatarSize);
                        avatarLoaded = true;
                    }
                } catch (e) {
                    console.log('Could not load avatar image:', e.message);
                }
            }
            
            // Draw placeholder if avatar didn't load
            if (!avatarLoaded) {
                const initial = (transaction.symbol || 'C').charAt(0).toUpperCase();
                drawAvatarPlaceholder((pageWidth - avatarSize) / 2, yPos, avatarSize, initial);
            }
            
            yPos += avatarSize + 10;

            // Title
            pdf.setTextColor(30, 30, 30);
            centerText(`${transaction.symbol} ${transaction.type?.toUpperCase() || 'Transaction'}`, yPos, 16, 'bold');
            yPos += 7;

            // Subtitle - Network
            pdf.setTextColor(100, 100, 100);
            centerText(transaction.network || '', yPos, 10, 'normal');
            yPos += 10;

            // Status badge
            const status = getStatusText().toUpperCase();
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            const statusWidth = pdf.getTextWidth(status) + 10;
            const statusX = (pageWidth - statusWidth) / 2;
            
            // Status background color
            if (transaction.confirmations >= 1) {
                pdf.setFillColor(198, 246, 213);
                pdf.setTextColor(34, 84, 61);
            } else {
                pdf.setFillColor(254, 235, 200);
                pdf.setTextColor(116, 66, 16);
            }
            
            pdf.roundedRect(statusX, yPos - 4, statusWidth, 7, 3, 3, 'F');
            pdf.text(status, statusX + 5, yPos + 1);
            yPos += 15;

            // Dashed line
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineDashPattern([2, 2], 0);
            pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
            yPos += 10;

            // Amount
            const amount = formatAmount(transaction.total_amount_sent, transaction.symbol);
            pdf.setTextColor(30, 30, 30);
            centerText(amount, yPos, 24, 'bold');
            yPos += 8;

            // Network info
            pdf.setTextColor(100, 100, 100);
            centerText(`${transaction.network} Network`, yPos, 10, 'normal');
            yPos += 12;

            // Dashed line
            pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
            yPos += 10;

            // Transaction details
            pdf.setTextColor(30, 30, 30);
            yPos = addDetailRow(t('Transaction ID'), transaction.txid, yPos, true);
            yPos = addDetailRow(t('Type'), transaction.type?.toUpperCase(), yPos);
            yPos = addDetailRow(t('Network'), transaction.network, yPos);
            yPos = addDetailRow(t('Address'), transaction.address, yPos, true);
            yPos = addDetailRow(t('Total Amount'), formatAmount(transaction.total_amount_sent, transaction.symbol), yPos);
            yPos = addDetailRow(t('Fee'), formatAmount(transaction.fee, transaction.symbol), yPos);
            yPos = addDetailRow(t('Confirmations'), transaction.confirmations || 0, yPos);
            yPos = addDetailRow(t('Block Number'), transaction.block_number || 'N/A', yPos);
            yPos = addDetailRow(t('Date'), transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : t('N/A'), yPos);
            yPos += 5;

            // Dashed line
            pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
            yPos += 10;

            // Footer
            pdf.setTextColor(100, 100, 100);
            centerText(t('Thank you for using BlockMap'), yPos, 10, 'normal');
            yPos += 6;
            centerText(transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : '', yPos, 8, 'normal');

            // Save PDF
            pdf.save(`BlockMap_Crypto_Receipt_${transaction.txid || 'transaction'}.pdf`);

            toast({
                title: t('Receipt downloaded successfully!'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast({
                title: t('Failed to download receipt'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    // Receipt View
    const ReceiptView = () => (
        <Box
            ref={receiptRef}
            bg={receiptBg}
            borderRadius="lg"
            p={6}
            border="1px dashed"
            borderColor={borderColor}
        >
            <VStack spacing={4} align="stretch">
                {/* Receipt Header */}
                <Flex direction="column" align="center" pb={4} borderBottom="1px dashed" borderColor={borderColor}>
                    {transaction.avatar && (
                        <Avatar src={transaction.avatar} size="lg" mb={3} />
                    )}
                    <Text fontSize="xl" fontWeight="bold" color={textColor}>
                        {transaction.symbol} {transaction.type?.toUpperCase()}
                    </Text>
                    <Text fontSize="sm" color={secondaryTextColor}>{transaction.network}</Text>
                    <Badge colorScheme={getStatusColor()} mt={2} fontSize="sm" px={3} py={1} borderRadius="full">
                        {getStatusText().toUpperCase()}
                    </Badge>
                </Flex>

                {/* Amount Section */}
                <Box textAlign="center" py={4} borderBottom="1px dashed" borderColor={borderColor}>
                    <Text fontSize="3xl" fontWeight="bold" color={textColor}>
                        {formatAmount(transaction.total_amount_sent, transaction.symbol)}
                    </Text>
                    <Text fontSize="sm" color={secondaryTextColor}>{transaction.network} Network</Text>
                </Box>

                {/* Receipt Details */}
                <VStack spacing={0} align="stretch">
                    <DetailRow label={t('Transaction ID')} value={transaction.txid} copyable />
                    <DetailRow label={t('Type')} value={transaction.type?.toUpperCase()} />
                    <DetailRow label={t('Network')} value={transaction.network} />
                    <DetailRow label={t('Address')} value={transaction.address} copyable />
                    <DetailRow label={t('Fee')} value={formatAmount(transaction.fee, transaction.symbol)} />
                    <DetailRow label={t('Confirmations')} value={transaction.confirmations || 0} />
                    <DetailRow label={t('Block Number')} value={transaction.block_number || 'N/A'} copyable />
                    <DetailRow label={t('Date')} value={formatDate(transaction.createdAt)} />
                </VStack>

                {/* Receipt Footer */}
                <Flex direction="column" align="center" pt={4} borderTop="1px dashed" borderColor={borderColor}>
                    <Text fontSize="xs" color={secondaryTextColor}>{t('Thank you for using BlockMap')}</Text>
                    <Text fontSize="xs" color={secondaryTextColor} mt={1}>
                        {formatDate(transaction.createdAt)}
                    </Text>
                </Flex>
            </VStack>
        </Box>
    );

    // Details View
    const DetailsView = () => (
        <VStack spacing={4} align="stretch">
            {/* Transaction Header */}
            <Flex align="center" gap={4}>
                {transaction.avatar && (
                    <Avatar src={transaction.avatar} size="md" />
                )}
                <Box>
                    <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        {transaction.symbol} {transaction.type?.toUpperCase()}
                    </Text>
                    <Text fontSize="sm" color={secondaryTextColor}>
                        {transaction.network}
                    </Text>
                </Box>
                <Badge colorScheme={getStatusColor()} ml="auto" fontSize="sm">
                    {getStatusText().toUpperCase()}
                </Badge>
            </Flex>

            <Divider />

            {/* Transaction Info */}
            <Box>
                <Text fontWeight="600" color={textColor} mb={3}>{t('Transaction Information')}</Text>
                <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
                    <DetailRow label={t('Transaction ID')} value={transaction.txid} copyable />
                    <DetailRow label={t('Type')} value={transaction.type?.toUpperCase()} />
                    <DetailRow label={t('Network')} value={transaction.network} />
                    <DetailRow label={t('From Address')} value={transaction.address} copyable />
                </VStack>
            </Box>

            {/* Amount Info */}
            <Box>
                <Text fontWeight="600" color={textColor} mb={3}>{t('Amount Details')}</Text>
                <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
                    <DetailRow label={t('Total Amount Sent')} value={formatAmount(transaction.total_amount_sent, transaction.symbol)} />
                    <DetailRow label={t('Fee')} value={formatAmount(transaction.fee, transaction.symbol)} />
                    {transaction.amounts_received && transaction.amounts_received.length > 0 && (
                        <DetailRow label={t('Amounts Received')} value={`${transaction.amounts_received.length} recipient(s)`} />
                    )}
                </VStack>
            </Box>

            {/* Status Info */}
            <Box>
                <Text fontWeight="600" color={textColor} mb={3}>{t('Status Information')}</Text>
                <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
                    <DetailRow label={t('Confirmations')} value={transaction.confirmations || 0} />
                    <DetailRow label={t('Block Number')} value={transaction.block_number || 'N/A'} copyable />
                    <DetailRow label={t('Confidence')} value={transaction.confidence ? `${(transaction.confidence * 100).toFixed(2)}%` : 'N/A'} />
                    <DetailRow label={t('Created At')} value={formatDate(transaction.createdAt)} />
                    <DetailRow label={t('Updated At')} value={formatDate(transaction.updatedAt)} />
                </VStack>
            </Box>
        </VStack>
    );

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
                <ModalContent bg={bgColor} borderRadius="xl">
                    <ModalHeader>
                        <Flex align="center" gap={2}>
                            <Icon as={showReceipt ? MdReceipt : ViewIcon} />
                            <Text>{showReceipt ? t('Transaction Receipt') : t('Transaction Details')}</Text>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {showReceipt ? <ReceiptView /> : <DetailsView />}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            mr={3}
                            onClick={() => setShowReceipt(!showReceipt)}
                            leftIcon={<Icon as={showReceipt ? ViewIcon : MdReceipt} />}
                        >
                            {showReceipt ? t('View Details') : t('View Receipt')}
                        </Button>
                        <Button
                            variant="outline"
                            colorScheme="green"
                            mr={3}
                            onClick={downloadReceipt}
                            leftIcon={<Icon as={MdDownload} />}
                            isLoading={isDownloading}
                            loadingText={t('Downloading...')}
                        >
                            {t('Download Receipt')}
                        </Button>
                        <Button colorScheme="brand" onClick={onClose}>
                            {t('Close')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

export default function CryptoTransactionsTable(props) {
    const { tableData, sortArray, index, searchQuery } = props;
    const { t } = useTranslation();
    const [sorting, setSorting] = React.useState([]);
    const [data, setData] = React.useState(tableData);
    const [{ pageIndex, pageSize }, setPagination] = React.useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedTransaction, setSelectedTransaction] = React.useState(null);

    const handleViewTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTransaction(null);
    };

    const textColor = useColorModeValue("secondaryGray.900", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    const bgHover = useColorModeValue("gray.50", "whiteAlpha.10");

    const copyToClipboard = React.useCallback((text, label) => {
        if (!text || text === t("N/A")) {
            constants.showToast("error", t("FAILED"), t("No data to copy"));
            return;
        }
        navigator.clipboard.writeText(text).then(() => {
            constants.showToast("success", t("COPIED"), `${label} ${t("copied to clipboard")}`);
        }).catch(() => {
            constants.showToast("error", t("FAILED"), t("Failed to copy"));
        });
    }, [t]);

    React.useEffect(() => {
        setData(tableData);
    }, [tableData]);

    const columns = [
        columnHelper.accessor("txid", {
            id: "txid",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("TXID")}
                </Text>
            ),
            cell: (info) => {
                const txid = info.getValue() || t("N/A");
                const displayTxid = txid && txid.length > 20
                    ? txid.substring(0, 6) + "..." + txid.substring(txid.length - 6)
                    : txid;
                return (
                    <Flex justify="center" align="center" gap={1}>
                        <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                            fontFamily="monospace"
                            title={txid}
                            cursor="pointer"
                        >
                            {displayTxid}
                        </Text>
                        {txid !== t("N/A") && (
                            <IconButton
                                size="sm"
                                icon={<CopyIcon />}
                                variant="ghost"
                                onClick={() => copyToClipboard(txid, t("Transaction ID"))}
                                _hover={{ bg: bgHover }}
                            />
                        )}
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("type", {
            id: "type",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("TYPE")}
                </Text>
            ),
            cell: (info) => {
                const value = info.getValue() || t("N/A");
                const typeMap = {
                    "send": { bg: "purple.100", color: "purple.800" },
                    "receive": { bg: "blue.100", color: "blue.800" },
                    "mint": { bg: "green.100", color: "green.800" },
                    "burn": { bg: "red.100", color: "red.800" },
                };
                const style = typeMap[value] || { bg: "gray.100", color: "gray.800" };
                return (
                    <Flex justify="center">
                        <Badge
                            bg={style.bg}
                            color={style.color}
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="11px"
                            fontWeight="700"
                            whiteSpace="nowrap"
                            textTransform="capitalize"
                        >
                            {value}
                        </Badge>
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("senders", {
            id: "senders",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600">
                    {t("FROM")}
                </Text>
            ),
            cell: (info) => {
                const senders = info.getValue() || [];
                const sender = senders[0] || t("N/A");
                const displaySender = sender && sender.length > 20
                    ? sender.substring(0, 6) + "..." + sender.substring(sender.length - 6)
                    : sender;
                const avatarData = info.row.original.avatar;

                return (
                    <Flex align="center" gap={2}>
                        {avatarData ? (
                            <Avatar
                                src={avatarData}
                                size="sm"
                            />
                        ) : (
                            <Avatar
                                name="N/A"
                                size="sm"
                            />
                        )}
                        <Flex align="center" gap={1}>
                            <Text
                                color={textColor}
                                fontSize="sm"
                                fontWeight="700"
                                fontFamily="monospace"
                                title={sender}
                            >
                                {displaySender}
                            </Text>
                            {sender !== t("N/A") && (
                                <IconButton
                                    size="sm"
                                    icon={<CopyIcon />}
                                    variant="ghost"
                                    onClick={() => copyToClipboard(sender, t("Sender address"))}
                                    _hover={{ bg: bgHover }}
                                />
                            )}
                        </Flex>
                    </Flex>
                );
            },
        }),
        // Network column removed per request
        columnHelper.accessor("amounts_sent", {
            id: "amounts_sent",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600">
                    {t("TO / AMOUNT")}
                </Text>
            ),
            cell: (info) => {
                const amounts = info.getValue() || [];
                if (amounts.length === 0) return <Text color={textColor} fontSize="sm" fontWeight="700">{t("N/A")}</Text>;

                const firstAmount = amounts[0];
                const recipient = firstAmount.recipient || t("N/A");
                const amount = firstAmount.amount || 0;
                const displayRecipient = recipient && recipient.length > 16
                    ? recipient.substring(0, 5) + "..." + recipient.substring(recipient.length - 5)
                    : recipient;

                return (
                    <Flex direction="column" gap={1}>
                        <Flex align="center" gap={1}>
                            <Text
                                color={textColor}
                                fontSize="sm"
                                fontWeight="700"
                                fontFamily="monospace"
                                title={recipient}
                            >
                                {displayRecipient}
                            </Text>
                            {recipient !== t("N/A") && (
                                <IconButton
                                    size="sm"
                                    icon={<CopyIcon />}
                                    variant="ghost"
                                    onClick={() => copyToClipboard(recipient, t("Recipient address"))}
                                    _hover={{ bg: bgHover }}
                                />
                            )}
                        </Flex>
                        <Text color="gray.500" fontSize="xs" fontWeight="600">
                            {amount}
                        </Text>
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("total_amount_sent", {
            id: "total_amount_sent",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600">
                    {t("TOTAL AMOUNT")}
                </Text>
            ),
            cell: (info) => {
                const amount = info.getValue();
                const symbol = info.row.original.symbol || "N/A";
                return (
                    <Flex align="center" gap={2}>
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                            {symbol} {Number(amount || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 8,
                            })}
                        </Text>
                    </Flex>
                );
            },
        }),
        // Fee column removed per request
        columnHelper.accessor("confirmations", {
            id: "confirmations",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("STATUS")}
                </Text>
            ),
            cell: (info) => {
                const confirmations = info.getValue() || 0;
                const isConfirmed = confirmations >= 1;
                const statusStyle = isConfirmed
                    ? { bg: "green.100", color: "green.800", icon: MdCheckCircle }
                    : { bg: "orange.100", color: "orange.800", icon: MdOutlineError };

                return (
                    <Flex justify="center" align="center">
                        {statusStyle.icon && (
                            <Icon
                                w="20px"
                                h="20px"
                                color={statusStyle.color.replace(".800", ".600")}
                                as={statusStyle.icon}
                            />
                        )}
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("createdAt", {
            id: "createdAt",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600">
                    {t("DATE")}
                </Text>
            ),
            cell: (info) => {
                const timestamp = info.getValue();
                let displayDate = t("N/A");

                if (timestamp) {
                    try {
                        const date = new Date(timestamp);
                        if (!isNaN(date.getTime())) {
                            const dateStr = date.toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                            });
                            const timeStr = date.toLocaleTimeString(undefined, {
                                hour: "numeric",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true,
                            });
                            displayDate = `${dateStr} ${timeStr}`;
                        }
                    } catch (e) {
                        displayDate = t("Invalid date");
                    }
                }

                return (
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                        {displayDate}
                    </Text>
                );
            },
        }),
        columnHelper.accessor("txid", {
            id: "actions",
            header: () => (
                <Text
                    justifyContent="space-between"
                    align="center"
                    fontSize={{ sm: "10px", lg: "12px" }}
                    color="gray.400"
                    fontWeight="600"
                >
                    {t("ACTIONS")}
                </Text>
            ),
            cell: (info) => (
                <Button
                    size="xs"
                    colorScheme="brand"
                    variant="outline"
                    leftIcon={<Icon as={ViewIcon} />}
                    fontSize="10px"
                    height="24px"
                    onClick={() => handleViewTransaction(info.row.original)}
                >
                    {t("View")}
                </Button>
            ),
        }),
    ];

    // Filter data based on search query
    const filteredData = React.useMemo(() => {
        if (!searchQuery || searchQuery.trim() === "") {
            return data;
        }
        const query = searchQuery.toLowerCase().trim();
        return data.filter((transaction) => {
            const hash = transaction.hash?.toLowerCase() || "";
            const fromAddress = transaction.from_address?.toLowerCase() || "";
            const toAddress = transaction.to_address?.toLowerCase() || "";
            const status = transaction.status?.toLowerCase() || "";
            const amount = transaction.amount?.toString().toLowerCase() || "";

            return hash.includes(query) || fromAddress.includes(query) || toAddress.includes(query) || status.includes(query) || amount.includes(query);
        });
    }, [data, searchQuery]);

    const table = useReactTable({
        data: filteredData,
        columns,
        state: {
            sorting,
            pagination: { pageSize, pageIndex },
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onPaginationChange: setPagination,
        setPageSize: pageSize,
        getPaginationRowModel: getPaginationRowModel(),
        debugTable: true,
    });

    React.useEffect(() => {
        table.setPageIndex(0);
    }, [tableData, table]);

    const getNextPage = () => {
        table.nextPage();
        setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
    };

    const getPreviousPage = () => {
        table.previousPage();
        setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
    };

    return (
        <>
            <Card
                flexDirection="column"
                w="100%"
                px="0px"
                overflow="hidden"
                boxShadow="0 2px 12px 0 rgba(0, 0, 0, 0.06)"
                borderRadius="16px"
            >
                <Flex px="25px" py="16px" justifyContent="space-between" align="center">
                    <Text
                        color={textColor}
                        fontSize="22px"
                        fontWeight="700"
                        lineHeight="100%"
                    >
                        {t("Transactions")}
                    </Text>
                </Flex>

                <Box overflowX="auto">
                    {data.length === 0 ? (
                        <Flex justifyContent="center" alignItems="center" height="300px">
                            <Text color={textColor} fontSize="sm" fontWeight="500">
                                {t("No results found")}! {t("Check")}{" "}
                                <NavLink style={{ color: "green" }} to={constants.urls.documentation}>
                                    {t("API documentation")}
                                </NavLink>{" "}
                                {t("for more details")}
                            </Text>
                        </Flex>
                    ) : (
                        <Table variant="simple" color="gray.500" mb="24px" mt="12px">
                            <Thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <Tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <Th
                                                    key={header.id}
                                                    colSpan={header.colSpan}
                                                    pe="10px"
                                                    borderColor={borderColor}
                                                    cursor="pointer"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    _hover={{ bg: bgHover }}
                                                >
                                                    <Flex
                                                        justifyContent="space-between"
                                                        align="center"
                                                        fontSize={{ sm: "10px", lg: "12px" }}
                                                        color="gray.400"
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: "",
                                                            desc: "",
                                                        }[header.column.getIsSorted()] ?? null}
                                                    </Flex>
                                                </Th>
                                            );
                                        })}
                                    </Tr>
                                ))}
                            </Thead>
                            <Tbody>
                                {table
                                    .getRowModel()
                                    .rows.slice(0, 20)
                                    .map((row) => {
                                        return (
                                            <Tr key={row.id} _hover={{ bg: bgHover }}>
                                                {row.getVisibleCells().map((cell) => {
                                                    return (
                                                        <Td
                                                            key={cell.id}
                                                            fontSize={{ sm: "12px", md: "14px" }}
                                                            minW={{ sm: "100px", md: "auto" }}
                                                            borderColor="transparent"
                                                            py="16px"
                                                            px="12px"
                                                        >
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </Td>
                                                    );
                                                })}
                                            </Tr>
                                        );
                                    })}
                            </Tbody>
                        </Table>
                    )}
                </Box>

                <Flex justifyContent="space-between" alignItems="center" px="25px" py="16px">
                    <Button
                        onClick={getPreviousPage}
                        isDisabled={!table.getCanPreviousPage()}
                        size="sm"
                        colorScheme="brand"
                        variant="outline"
                    >
                        {t("Previous")}
                    </Button>
                    <Text fontSize="sm" fontWeight="500" color={textColor}>
                        {t("Page")} {table.getState().pagination.pageIndex + 1} {t("of")}{" "}
                        {table.getPageCount()}
                    </Text>
                    <Button
                        onClick={getNextPage}
                        isDisabled={!table.getCanNextPage()}
                        size="sm"
                        colorScheme="brand"
                        variant="outline"
                    >
                        {t("Next")}
                    </Button>
                    <Select
                        value={pageSize}
                        width="120px"
                        onChange={(e) => {
                            setPagination((prev) => ({
                                ...prev,
                                pageSize: Number(e.target.value),
                            }));
                        }}
                        size="sm"
                    >
                        {sortArray.map((size) => (
                            <option key={size} value={size}>
                                {t("Show")} {size} {t("Max")}
                            </option>
                        ))}
                    </Select>
                </Flex>
            </Card>
            <TransactionDetailsModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                transaction={selectedTransaction}
                t={t}
            />
        </>
    );
}
